import React from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress } from "@mui/material";
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { colors, customShadows, borderRadiusPx } from "../../../../theme";

/**
 * CommonButton Component
 * Centralized, reusable button component adhering to the AutoBET theme palette & elevation tokens.
 * All colors, padding, borders, shadows, and hover transitions are managed centrally.
 *
 * Supported variants:
 * - "primary" / "contained": Solid brand blue fill with hover glow elevation.
 * - "play" / "playNow" / "playLottery" / "playThisLottery" / "gamePlay" / "gradientPrimary" / "gradient": Bold gradient play button with play icon and elevated glow.
 * - "outlined" / "outline" (default): Subtle card border, clean slate text, primary hover glow.
 * - "secondary" / "dark": Solid slate 900 background with dark hover elevation.
 * - "action" / "actionOutlined": Elevated card-style action button with light blue accent.
 * - "purple" / "purpleAction" / "actionPurple" / "purpleOutlined" / "purpleSubtle": Violet/purple action button with subtle purple tint and purple hover glow.
 * - "purpleSolid" / "purpleContained": Solid purple background with white text.
 * - "amber" / "amberAction" / "actionAmber" / "amberOutlined" / "warningAction": Warm amber action button with subtle amber tint and amber hover glow.
 * - "emerald" / "emeraldAction" / "actionEmerald" / "emeraldOutlined" / "successAction": Fresh emerald green action button.
 * - "refresh" / "toolbar" / "neutralOutlined": Paper background, subtle border, slate 700 text turning brand blue on hover.
 * - "text": Clean transparent button with brand blue hover tint.
 * - "reset" / "cancel" / "ghost" / "secondaryText" / "mutedText": Neutral slate text button for dialog cancel, popup actions, and filter resets.
 * - "clear" / "clearAction" / "actionClear" / "dangerAction" / "dangerSubtle" / "resetAction": Subtle slate card button that transitions to a gentle red highlight on hover.
 * - "danger" / "dangerOutlined": Red alert border and text with red-50 hover tint.
 * - "dangerText" / "dangerGhost": Red transparent text button with red-50 hover tint.
 * - "dangerSolid" / "dangerContained": Solid red background with white text.
 */
const CommonButton = ({
  children,
  label,
  variant = "outlined",
  size = "medium",
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  onClick,
  fullWidth = false,
  sx = {},
  ...props
}) => {
  // Size-based padding, font sizes & height
  const sizeStyles =
    {
      small: {
        fontSize: "0.78rem",
        px: 2,
        py: 0.75,
        minHeight: "34px",
        borderRadius: borderRadiusPx?.lg || "10px",
      },
      input: {
        fontSize: "0.85rem",
        px: 2.5,
        py: 1,
        minHeight: "40px",
        borderRadius: borderRadiusPx?.xl || "12px",
      },
      medium: {
        fontSize: "0.875rem",
        px: 2.75,
        py: 1.1,
        minHeight: "42px",
        borderRadius: borderRadiusPx?.xl || "12px",
      },
      large: {
        fontSize: "0.95rem",
        px: 3.5,
        py: 1.35,
        minHeight: "48px",
        borderRadius: borderRadiusPx?.xl || "12px",
      },
      hero: {
        fontSize: "0.95rem",
        px: 4.5,
        py: 1.25,
        minHeight: "48px",
        borderRadius: "50px",
        minWidth: "180px",
      },
    }[size] || {
      fontSize: "0.875rem",
      px: 2.75,
      py: 1.1,
      minHeight: "42px",
      borderRadius: borderRadiusPx?.xl || "12px",
    };

  // Variant-specific styling rules
  let variantStyles = {};
  let muiVariant = "outlined";

  switch (variant) {
    case "contained":
    case "primary":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.primary.main,
        color: colors.primary.contrastText,
        boxShadow: customShadows.buttonPrimary,
        borderRadius: borderRadiusPx?.xl || "12px",
        border: "none",
        "&:hover": {
          backgroundColor: colors.primary.dark,
          boxShadow: customShadows.buttonPrimaryHover,
        },
      };
      break;

    case "play":
    case "playNow":
    case "playLottery":
    case "playThisLottery":
    case "gamePlay":
    case "gradientPrimary":
    case "gradient":
      muiVariant = "contained";
      variantStyles = {
        background: colors.gradients.primary,
        color: colors.neutral.white,
        fontWeight: 700,
        fontSize: "0.95rem",
        px: 3,
        py: 1,
        border: "none",
        borderRadius: borderRadiusPx.xl,
        boxShadow: customShadows.buttonPrimary,
        "& .MuiButton-startIcon": {
          color: "inherit",
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
        },
        "&:hover": {
          background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
          boxShadow: customShadows.buttonPrimaryHover,
          transform: "translateY(-1px)",
        },
        "&.Mui-disabled": {
          background: colors.slate[200],
          color: colors.slate[400],
          boxShadow: "none",
        },
      };
      break;

    case "secondary":
    case "dark":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.slate[900],
        color: colors.primary.contrastText,
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        border: "none",
        "&:hover": {
          backgroundColor: colors.slate[800],
          boxShadow: customShadows.buttonDarkHover,
        },
      };
      break;

    case "action":
    case "actionOutlined":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.dashboard.cardBorder}`,
        borderColor: colors.dashboard.cardBorder,
        backgroundColor: colors.dashboard.cardBg,
        color: colors.primary.main,
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          borderColor: colors.blue[300],
          backgroundColor: colors.blue[50],
          boxShadow: customShadows.buttonActionHover,
        },
      };
      break;

    case "blueAction":
    case "actionBlue":
    case "primaryAction":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.blue[200]}`,
        borderColor: colors.blue[200],
        backgroundColor: colors.blue[50],
        color: colors.primary.main,
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        "& .MuiButton-startIcon": {
          color: "inherit",
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
        },
        "&:hover": {
          borderColor: colors.primary.main,
          backgroundColor: colors.primary.main,
          color: "#ffffff",
          boxShadow: "0 4px 14px rgba(37, 99, 235, 0.25)",
          "& .MuiButton-startIcon": {
            color: "inherit",
          },
          "& .MuiButton-endIcon": {
            color: "inherit",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: colors.slate[100],
          borderColor: colors.slate[200],
          color: colors.slate[400],
          "& .MuiButton-startIcon": {
            color: colors.slate[400],
          },
        },
      };
      break;

    case "refresh":
    case "toolbar":
    case "neutralOutlined":
      muiVariant = "outlined";
      variantStyles = {
        backgroundColor: colors.background.paper,
        borderColor: colors.dashboard.cardBorder,
        color: colors.slate[700],
        borderRadius: borderRadiusPx?.xl || "12px",
        boxShadow: customShadows.cardFlat,
        "&:hover": {
          borderColor: colors.primary.main,
          backgroundColor: colors.blue[50],
          color: colors.primary.main,
        },
      };
      break;

    case "text":
      muiVariant = "text";
      variantStyles = {
        color: colors.primary.main,
        backgroundColor: "transparent",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          backgroundColor: colors.blue[50],
        },
      };
      break;

    case "reset":
    case "cancel":
    case "ghost":
    case "secondaryText":
    case "mutedText":
    case "neutralText":
      muiVariant = "text";
      variantStyles = {
        color: colors.slate[600],
        backgroundColor: "transparent",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          backgroundColor: colors.slate[100],
          color: colors.slate[900],
        },
        "&.Mui-disabled": {
          color: colors.slate[400],
        },
      };
      break;

    case "dangerText":
    case "dangerGhost":
      muiVariant = "text";
      variantStyles = {
        color: colors.dashboard.status.dangerText,
        backgroundColor: "transparent",
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          backgroundColor: colors.red[50],
          color: colors.red[700],
        },
        "&.Mui-disabled": {
          color: colors.slate[400],
        },
      };
      break;

    case "dangerSolid":
    case "dangerContained":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.error.main,
        color: "#ffffff",
        borderRadius: borderRadiusPx?.xl || "12px",
        boxShadow: customShadows.cardFlat,
        border: "none",
        "&:hover": {
          backgroundColor: colors.error.dark,
          boxShadow: customShadows.buttonDarkHover,
        },
      };
      break;

    case "clear":
    case "clearAction":
    case "actionClear":
    case "dangerAction":
    case "dangerSubtle":
    case "dangerOutlinedSubtle":
    case "resetAction":
    case "actionReset":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.slate[200]}`,
        borderColor: colors.slate[200],
        backgroundColor: colors.slate[50],
        color: colors.slate[600],
        boxShadow: "none",
        borderRadius: borderRadiusPx?.xl || "12px",
        outline: "none",
        "& .MuiButton-startIcon": {
          color: "inherit",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "&:hover": {
          borderColor: colors.red[300],
          backgroundColor: colors.red[50],
          color: colors.red[600],
          outline: "none",
          boxShadow: "none",
          "& .MuiButton-startIcon": {
            color: "inherit",
            "& .MuiSvgIcon-root": {
              color: "inherit",
            },
          },
          "& .MuiButton-endIcon": {
            color: "inherit",
            "& .MuiSvgIcon-root": {
              color: "inherit",
            },
          },
        },
        "&:focus, &:active": {
          borderColor: colors.red[300],
          outline: "none",
          boxShadow: "none",
        },
        "&.Mui-disabled": {
          borderColor: colors.slate[200],
          backgroundColor: colors.slate[100],
          color: colors.slate[400],
          boxShadow: "none",
          "& .MuiButton-startIcon": {
            color: colors.slate[400],
            "& .MuiSvgIcon-root": {
              color: colors.slate[400],
            },
          },
          "& .MuiButton-endIcon": {
            color: colors.slate[400],
            "& .MuiSvgIcon-root": {
              color: colors.slate[400],
            },
          },
        },
      };
      break;

    case "danger":
    case "dangerOutlined":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.red[200]}`,
        borderColor: colors.red[200],
        color: colors.red[600],
        backgroundColor: colors.background.paper,
        borderRadius: borderRadiusPx?.xl || "12px",
        "& .MuiButton-startIcon": {
          color: "inherit",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "&:hover": {
          borderColor: colors.red[400],
          backgroundColor: colors.red[50],
          color: colors.red[700],
          "& .MuiButton-startIcon": {
            color: "inherit",
            "& .MuiSvgIcon-root": {
              color: "inherit",
            },
          },
        },
        "&.Mui-disabled": {
          borderColor: colors.slate[200],
          backgroundColor: colors.slate[100],
          color: colors.slate[400],
          "& .MuiButton-startIcon": {
            color: colors.slate[400],
          },
        },
      };
      break;

    case "purple":
    case "purpleAction":
    case "actionPurple":
    case "purpleOutlined":
    case "purpleSubtle":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.purple[200]}`,
        borderColor: colors.purple[200],
        backgroundColor: colors.purple[50],
        color: colors.purple[700],
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        "& .MuiButton-startIcon": {
          color: "inherit",
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
        },
        "&:hover": {
          borderColor: colors.purple[600],
          backgroundColor: colors.purple[600],
          color: "#ffffff",
          boxShadow: "0 4px 14px rgba(124, 58, 237, 0.25)",
          "& .MuiButton-startIcon": {
            color: "inherit",
          },
          "& .MuiButton-endIcon": {
            color: "inherit",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: colors.slate[100],
          borderColor: colors.slate[200],
          color: colors.slate[400],
          "& .MuiButton-startIcon": {
            color: colors.slate[400],
          },
        },
      };
      break;

    case "purpleSolid":
    case "purpleContained":
      muiVariant = "contained";
      variantStyles = {
        backgroundColor: colors.purple[600],
        color: "#ffffff",
        border: "none",
        borderRadius: borderRadiusPx?.xl || "12px",
        boxShadow: "0 4px 14px rgba(124, 58, 237, 0.25)",
        "&:hover": {
          backgroundColor: colors.purple[700],
          boxShadow: "0 6px 20px rgba(124, 58, 237, 0.35)",
        },
        "&.Mui-disabled": {
          backgroundColor: colors.slate[200],
          color: colors.slate[400],
        },
      };
      break;

    case "amber":
    case "amberAction":
    case "actionAmber":
    case "amberOutlined":
    case "warningAction":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.amber[200]}`,
        borderColor: colors.amber[200],
        backgroundColor: colors.amber[50],
        color: colors.amber[700],
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        "& .MuiButton-startIcon": {
          color: "inherit",
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
        },
        "&:hover": {
          borderColor: colors.amber[600],
          backgroundColor: colors.amber[600],
          color: "#ffffff",
          boxShadow: "0 4px 14px rgba(217, 119, 6, 0.25)",
          "& .MuiButton-startIcon": {
            color: "inherit",
          },
          "& .MuiButton-endIcon": {
            color: "inherit",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: colors.slate[100],
          borderColor: colors.slate[200],
          color: colors.slate[400],
          "& .MuiButton-startIcon": {
            color: colors.slate[400],
          },
        },
      };
      break;

    case "emerald":
    case "emeraldAction":
    case "actionEmerald":
    case "emeraldOutlined":
    case "successAction":
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.emerald[200]}`,
        borderColor: colors.emerald[200],
        backgroundColor: colors.emerald[50],
        color: colors.emerald[700],
        boxShadow: customShadows.cardFlat,
        borderRadius: borderRadiusPx?.xl || "12px",
        "& .MuiButton-startIcon": {
          color: "inherit",
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
        },
        "&:hover": {
          borderColor: colors.emerald[600],
          backgroundColor: colors.emerald[600],
          color: "#ffffff",
          boxShadow: "0 4px 14px rgba(5, 150, 105, 0.25)",
          "& .MuiButton-startIcon": {
            color: "inherit",
          },
          "& .MuiButton-endIcon": {
            color: "inherit",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: colors.slate[100],
          borderColor: colors.slate[200],
          color: colors.slate[400],
          "& .MuiButton-startIcon": {
            color: colors.slate[400],
          },
        },
      };
      break;

    case "outline":
    case "outlined":
    default:
      muiVariant = "outlined";
      variantStyles = {
        border: `1px solid ${colors.dashboard.cardBorder}`,
        borderColor: colors.dashboard.cardBorder,
        backgroundColor: colors.background.paper,
        color: colors.slate[700],
        borderRadius: borderRadiusPx?.xl || "12px",
        "&:hover": {
          borderColor: colors.primary.main,
          backgroundColor: colors.blue[50],
          color: colors.primary.main,
        },
      };
      break;
  }

  const combinedHover = {
    ...(variantStyles["&:hover"] || {}),
    letterSpacing: "0.02em",
    "& .MuiButton-endIcon": {
      transform: "translateX(4px)",
      color: "inherit",
    },
    "& .MuiButton-startIcon": {
      color: "inherit",
    },
    "& .MuiSvgIcon-root": {
      color: "inherit",
    },
  };

  // Determine active start icon (auto-loading spinner / refresh / play icon fallback)
  const renderStartIcon = () => {
    if (loading) {
      return (
        <CircularProgress
          size={size === "small" ? 14 : 16}
          color="inherit"
        />
      );
    }
    if (startIcon !== undefined) {
      return startIcon;
    }
    if (variant === "refresh") {
      return <RefreshIcon sx={{ fontSize: size === "small" ? 16 : 18 }} />;
    }
    if (
      variant === "play" ||
      variant === "playNow" ||
      variant === "playLottery" ||
      variant === "playThisLottery" ||
      variant === "gamePlay"
    ) {
      return <PlayArrowIcon sx={{ fontSize: size === "small" ? 18 : 20 }} />;
    }
    return null;
  };

  const buttonContent = children ?? label;

  return (
    <Button
      variant={muiVariant}
      color="inherit"
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={renderStartIcon()}
      endIcon={!loading ? endIcon : null}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.01em",
        boxSizing: "border-box",
        transition: "all 0.2s ease-in-out",
        "& .MuiButton-startIcon": {
          color: "inherit",
        },
        "& .MuiButton-endIcon": {
          color: "inherit",
          transition: "transform 0.2s ease-in-out",
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        "& .MuiSvgIcon-root": {
          color: "inherit",
        },
        ...sizeStyles,
        ...variantStyles,
        "&:hover": combinedHover,
        ...sx,
      }}
      {...props}
    >
      {buttonContent}
    </Button>
  );
};

CommonButton.propTypes = {
  children: PropTypes.node,
  label: PropTypes.node,
  variant: PropTypes.oneOf([
    "outlined",
    "outline",
    "contained",
    "primary",
    "play",
    "playNow",
    "playLottery",
    "playThisLottery",
    "gamePlay",
    "gradientPrimary",
    "gradient",
    "secondary",
    "dark",
    "action",
    "actionOutlined",
    "blueAction",
    "actionBlue",
    "primaryAction",
    "purple",
    "purpleAction",
    "actionPurple",
    "purpleOutlined",
    "purpleSubtle",
    "purpleSolid",
    "purpleContained",
    "amber",
    "amberAction",
    "actionAmber",
    "amberOutlined",
    "warningAction",
    "emerald",
    "emeraldAction",
    "actionEmerald",
    "emeraldOutlined",
    "successAction",
    "refresh",
    "toolbar",
    "neutralOutlined",
    "text",
    "reset",
    "cancel",
    "ghost",
    "secondaryText",
    "mutedText",
    "neutralText",
    "clear",
    "clearAction",
    "actionClear",
    "dangerAction",
    "dangerSubtle",
    "dangerOutlinedSubtle",
    "resetAction",
    "actionReset",
    "danger",
    "dangerOutlined",
    "dangerText",
    "dangerGhost",
    "dangerSolid",
    "dangerContained",
  ]),
  size: PropTypes.oneOf(["small", "input", "medium", "large", "hero"]),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};

export default CommonButton;
